import { api, unwrapList } from '../../shared/lib/axios';

export type CauServiceStatus =
  | 'draft'
  | 'in_review'
  | 'awaiting_approval'
  | 'scheduled'
  | 'published'
  | 'inactive'
  | 'archived';

export type CauFormField = {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'boolean';
  required?: boolean;
  options?: string[];
};

export type CauCatalogService = {
  id: string;
  serviceId: string;
  code: string;
  version: number;
  name: string;
  description: string;
  subcategory: string | null;
  category: { id: string; code: string; name: string };
  queue: { id: string; name: string };
  documentRequirements: Array<{ name: string; required: boolean }>;
  approvalRequired: boolean;
  sla: { applicable?: boolean; responseMinutes?: number; resolutionMinutes?: number };
  instructions: string | null;
  restrictions: string | null;
  formSchema?: { fields: CauFormField[] };
};

export type CauCategory = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
};

export type CauQueue = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
};

export type CauAdminServiceVersion = {
  id: string;
  serviceId: string;
  version: number;
  name: string;
  description: string;
  subcategory: string | null;
  status: CauServiceStatus;
  queueId: string;
  queue: CauQueue;
  service: { id: string; code: string; categoryId: string; category: CauCategory };
  audienceRoleSlugs: string[];
  audienceEmploymentTypes: string[];
  formSchema: { fields: CauFormField[] };
  approvalConfig: { required?: boolean; separationOfDuties?: boolean; steps?: unknown[] };
  slaConfig: { applicable?: boolean; responseMinutes?: number; resolutionMinutes?: number };
  effectiveFrom: string | null;
  effectiveTo: string | null;
};

export type CauTicketStatus =
  | 'draft' | 'open' | 'triage' | 'waiting_approval' | 'waiting_service'
  | 'in_service' | 'waiting_requester' | 'waiting_collaborator' | 'waiting_vendor'
  | 'transfer_pending' | 'resolved' | 'reopened' | 'rejected' | 'cancelled' | 'closed';

export type CauTicketLevel = 'low' | 'medium' | 'high' | 'critical';

export type CauTicketAttachment = {
  id: string;
  logicalName: string;
  version: number;
  fileName: string;
  mimeType: string;
  sizeBytes: string;
  sha256: string;
  classification: 'internal' | 'confidential' | 'restricted';
  scanStatus: 'pending_scan' | 'clean' | 'quarantined' | 'failed';
  createdAt: string;
};

export type CauTicket = {
  id: string;
  protocol: string | null;
  status: CauTicketStatus;
  priority: CauTicketLevel | null;
  impact: CauTicketLevel;
  urgency: CauTicketLevel;
  serviceVersionId: string;
  service: { name: string | null; code: string | null; category: { name?: string } | null };
  requester: { id?: string; name?: string };
  beneficiary: { id?: string; name?: string };
  queue: { id: string; name: string | null };
  resolutionDueAt: string | null;
  resolutionCycle?: number;
  validationDueAt?: string | null;
  reopenUntil?: string | null;
  closedAt?: string | null;
  resolutionSummary?: string | null;
  resolutionEvidence?: Record<string, unknown>;
  cancellationRequestedAt: string | null;
  pending: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  formData?: Record<string, unknown>;
  serviceSnapshot?: CauCatalogService & { formSchema?: { fields: CauFormField[] } };
  slaSnapshot?: Record<string, unknown>;
  attachments?: CauTicketAttachment[];
  history?: Array<{ id: string; event: string; visibility?: string; payload: Record<string, unknown>; createdAt: string }>;
  sla?: CauSlaClock[];
};

export type CauSlaClock = {
  id: string;
  metric: 'triage' | 'first_response' | 'resolution';
  targetMinutes: number;
  consumedMinutes: number;
  remainingMinutes: number;
  status: 'running' | 'paused' | 'met' | 'breached' | 'cancelled';
  deadlineAt: string;
  progressPercent: number;
  pausedAt?: string | null;
  metAt?: string | null;
  breachedAt?: string | null;
};

export type CauOperationalQueue = { id: string; code: string; name: string; description: string | null; openTickets: number };
export type CauQueueMember = { id: string; userId: string; name: string; email: string; role: 'agent' | 'triager' | 'manager' };
export type CauQueueCandidate = { id: string; name: string; email: string; role: string };
export type CauOperationalTicket = CauTicket & {
  assignee: { id: string; name?: string; email?: string } | null;
  isPotentialIncident: boolean;
  duplicateOfTicketId: string | null;
  resolutionSummary?: string | null;
  resolutionEvidence?: Record<string, unknown>;
  assignments?: Array<Record<string, unknown>>;
  transfers?: Array<{ id: string; status: string; justification: string; decisionReason?: string | null; destinationQueue: CauQueue; sourceQueue: CauQueue; createdAt: string }>;
  collaborations?: Array<Record<string, unknown>>;
  history?: Array<{ id: string; event: string; visibility?: string; payload: Record<string, unknown>; createdAt: string }>;
};

export type CauInteraction = {
  id: string;
  kind: 'message' | 'correction' | 'request_information' | 'provide_information' | 'clarification';
  visibility: 'public' | 'internal' | 'restricted';
  body: string;
  correctsInteractionId: string | null;
  audienceUserIds: string[];
  classification: string;
  author: { id: string; name?: string } | null;
  createdAt: string;
};

export type CauSatisfactionSurvey = {
  id: string; code: string; version: number; answered: boolean;
  schema: { questions: Array<{ key: string; label: string; type: 'rating' | 'text'; required?: boolean; min?: number; max?: number }> };
};

export type CauApprovalStep = {
  id: string; instanceId: string; key: string; name: string; sequence: number; groupKey: string;
  strategy: 'all' | 'any' | 'quorum'; quorum: number; status: string; dueAt: string | null; decidedAt: string | null;
  ticket?: { id: string; protocol: string | null; service: { name?: string }; requester: { name?: string }; beneficiary: { name?: string } };
};

export type CauAccessRequest = {
  id: string; ticketId: string; beneficiaryId: string; operation: 'create' | 'change' | 'revoke';
  accessType: 'regular' | 'special' | 'privileged' | 'temporary' | 'emergency'; systemCode: string; systemName: string;
  profileCode: string; profileName: string; scope: Record<string, unknown>; riskLevel: 'low' | 'medium' | 'high' | 'critical';
  justification: string; validFrom: string | null; validUntil: string | null; requiresMfa: boolean; mfaAvailable: boolean;
  monitoringRequired: boolean; emergencyExecution: boolean; status: string; provisionedAt: string | null; revokedAt: string | null;
  ticket?: { id: string; protocol: string | null; status: string };
};

export type CauAccessInput = Pick<CauAccessRequest, 'operation' | 'accessType' | 'systemCode' | 'systemName' | 'profileCode' | 'profileName' | 'scope' | 'riskLevel' | 'justification'> & {
  validFrom?: string; validUntil?: string; requiresMfa?: boolean; mfaAvailable?: boolean; monitoringRequired?: boolean; emergencyExecution?: boolean;
};
export type CauAssetSummary = { id:string; assetCode:string; displayCode:string|null; serialNumber:string|null; description:string; status:string; assetType:string; category?:{id:string;name:string}|null; currentCustodian?:{id:string;name:string;userId?:string|null}|null; location?:{id:string;name:string}|null; companyId?:string|null };
export type CauResourceLink = { id:string; ticketId:string; resourceType:'asset'|'purchase_request'|'project'|'client'|'other'; externalId:string; isPrimary:boolean; snapshot:CauAssetSummary; createdAt:string };
export type CauAssetContainment = { id:string; action:string; description:string; status:string; dueAt:string|null; completedAt:string|null; evidence:Record<string,unknown> };
export type CauAssetOperation = { id:string; ticketId:string; resourceLinkId:string; assetId:string; type:string; status:string; targetCustodianId:string|null; replacementAssetId:string|null; relatedPurchaseRequestId:string|null; temporaryUntil:string|null; details:Record<string,unknown>; evidence:Record<string,unknown>; currentAcceptedAt:string|null; targetAcceptedAt:string|null; termsAcceptedAt:string|null; inspectionConfirmedAt:string|null; rejectionReason:string|null; executedAt:string|null; asset?:CauAssetSummary; targetCustodian?:{id:string;name:string}|null; containments?:CauAssetContainment[] };
export type CauAssetCustodian = { id:string; name:string; email:string|null; jobTitle:string|null; userId:string|null };
export type CauNotificationPreference = { userId:string; emailEnabled:boolean; teamsEnabled:boolean; digestEnabled:boolean; mutedEventTypes:string[] };
export type CauNotificationDelivery = { id:string; channel:'internal'|'email'|'teams'; status:string; attempts:number; lastError:string|null; deliveredAt:string|null; createdAt:string; outboxEvent?:{type:string;payload:Record<string,unknown>} };
export type CauAuditEvent = { id:string; correlationId:string|null; actorType:string; actor?:{name?:string;email?:string}|null; actorSnapshot:Record<string,unknown>; action:string; entityType:string; entityId:string|null; ticketId:string|null; oldValue:Record<string,unknown>|null; newValue:Record<string,unknown>|null; justification:string|null; result:string; classification:string; previousHash:string|null; eventHash:string|null; createdAt:string };
export type CauAnalyticsFilters={from?:string;to?:string;queueId?:string;serviceVersionId?:string;status?:string;priority?:string};
export type CauDashboard={asOf:string;methodology:{version:string;resolution:string;sla:string;satisfaction:string;timezone:string};summary:{total:number;open:number;resolved:number;closed:number;avgResolutionMinutes:number;slaCompliancePercent:number|null;satisfactionAverage:number|null;satisfactionResponses:number};byStatus:Array<{status:string;count:string}>;byPriority:Array<{priority:string;count:string}>;byQueue:Array<{id:string;name:string;count:string}>;byService:Array<{id:string;name:string;count:string}>;timeline:Array<{day:string;created:string;resolved:string;closed:string}>};
export type CauReportJob={id:string;reportType:'tickets'|'sla'|'satisfaction';format:'csv'|'xlsx'|'pdf';status:'pending'|'processing'|'completed'|'failed'|'expired';purpose:string;rowCount:number;fileName:string|null;errorMessage:string|null;expiresAt:string;createdAt:string;completedAt:string|null};
export type CauReportSchedule={id:string;name:string;reportType:string;format:string;frequency:string;recipientUserIds:string[];nextRunAt:string;lastRunAt:string|null;isActive:boolean};

export type CauTicketList = {
  data: CauTicket[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
};

function entity<T>(body: T & { data?: T }): T {
  return body.data ?? body;
}

export async function listCatalog(search = '', categoryId = ''): Promise<CauCatalogService[]> {
  const { data } = await api.get('/cau/catalog/services', { params: { search: search || undefined, categoryId: categoryId || undefined } });
  return unwrapList<CauCatalogService>(data);
}

export async function getCatalogService(id: string): Promise<CauCatalogService> {
  const { data } = await api.get(`/cau/catalog/services/${id}`);
  return entity(data);
}

export async function listCategories(): Promise<CauCategory[]> {
  const { data } = await api.get('/cau/admin/categories');
  return unwrapList<CauCategory>(data);
}

export async function listQueues(): Promise<CauQueue[]> {
  const { data } = await api.get('/cau/admin/queues');
  return unwrapList<CauQueue>(data);
}

export async function listAdminServices(): Promise<CauAdminServiceVersion[]> {
  const { data } = await api.get('/cau/admin/services');
  return unwrapList<CauAdminServiceVersion>(data);
}

export async function createCategory(input: { code: string; name: string; description?: string }) {
  const { data } = await api.post('/cau/admin/categories', input);
  return entity<CauCategory>(data);
}

export async function createQueue(input: { code: string; name: string; description?: string }) {
  const { data } = await api.post('/cau/admin/queues', input);
  return entity<CauQueue>(data);
}

export type CreateCauServiceInput = {
  code: string;
  categoryId: string;
  name: string;
  description: string;
  subcategory?: string;
  queueId: string;
  audienceRoleSlugs: string[];
  audienceEmploymentTypes: string[];
  formSchema: { fields: CauFormField[] };
  documentRequirements: Array<{ name: string; required: boolean }>;
  routingConfig: Record<string, unknown>;
  approvalConfig: Record<string, unknown>;
  slaConfig: Record<string, unknown>;
  notificationConfig: Record<string, unknown>;
  instructions?: string;
  restrictions?: string;
};

export async function createService(input: CreateCauServiceInput) {
  const { data } = await api.post('/cau/admin/services', input);
  return entity<CauAdminServiceVersion>(data);
}

export async function updateServiceVersion(id: string, input: Partial<CreateCauServiceInput>) {
  const { data } = await api.patch(`/cau/admin/service-versions/${id}`, input);
  return entity<CauAdminServiceVersion>(data);
}

export async function runLifecycle(id: string, action: string, justification?: string) {
  const { data } = await api.post(`/cau/admin/service-versions/${id}/${action}`, { justification });
  return entity<CauAdminServiceVersion>(data);
}

export async function validateService(id: string): Promise<{ valid: boolean; issues: string[] }> {
  const { data } = await api.post(`/cau/admin/service-versions/${id}/validate`);
  return entity(data);
}

export async function simulateService(id: string): Promise<{ valid: boolean; issues: string[]; result: Record<string, unknown> }> {
  const { data } = await api.post(`/cau/admin/service-versions/${id}/simulate`);
  return entity(data);
}

export async function createNextVersion(serviceId: string) {
  const { data } = await api.post(`/cau/admin/services/${serviceId}/versions`);
  return entity<CauAdminServiceVersion>(data);
}

export async function createTicketDraft(input: {
  serviceVersionId: string;
  beneficiaryId?: string;
  impact: CauTicketLevel;
  urgency: CauTicketLevel;
  formData: Record<string, unknown>;
}) {
  const { data } = await api.post('/cau/tickets/drafts', input);
  return entity<CauTicket>(data);
}

export async function updateTicketDraft(id: string, input: {
  beneficiaryId?: string;
  impact?: CauTicketLevel;
  urgency?: CauTicketLevel;
  formData?: Record<string, unknown>;
}) {
  const { data } = await api.patch(`/cau/tickets/drafts/${id}`, input);
  return entity<CauTicket>(data);
}

export async function submitTicketDraft(id: string) {
  const { data } = await api.post(`/cau/tickets/drafts/${id}/submit`);
  return entity<CauTicket>(data);
}

export async function listTickets(params: { status?: string; search?: string; page?: number; pageSize?: number } = {}): Promise<CauTicketList> {
  const { data } = await api.get('/cau/tickets', { params });
  return { data: unwrapList<CauTicket>(data), meta: data.meta ?? { page: 1, pageSize: 20, total: 0, totalPages: 0 } };
}

export async function getTicket(id: string): Promise<CauTicket> {
  const { data } = await api.get(`/cau/tickets/${id}`);
  return entity<CauTicket>(data);
}

export async function requestTicketCancellation(id: string, reason: string) {
  const { data } = await api.post(`/cau/tickets/${id}/cancellation-requests`, { reason });
  return entity<CauTicket>(data);
}

export async function uploadTicketAttachment(
  ticketId: string,
  file: File,
  logicalName: string,
  classification: CauTicketAttachment['classification'] = 'internal',
) {
  const form = new FormData();
  form.append('file', file);
  form.append('logicalName', logicalName);
  form.append('classification', classification);
  const { data } = await api.post(`/cau/tickets/${ticketId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return entity<CauTicketAttachment>(data);
}

export async function removeTicketAttachment(ticketId: string, attachmentId: string) {
  await api.delete(`/cau/tickets/${ticketId}/attachments/${attachmentId}`);
}

export async function downloadTicketAttachment(ticketId: string, attachment: CauTicketAttachment) {
  const response = await api.get(`/cau/tickets/${ticketId}/attachments/${attachment.id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = attachment.fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadOperationalAttachment(ticketId: string, attachment: CauTicketAttachment) {
  const response = await api.get(`/cau/operations/tickets/${ticketId}/attachments/${attachment.id}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = attachment.fileName; anchor.click();
  URL.revokeObjectURL(url);
}

export async function listOperationalQueues(): Promise<CauOperationalQueue[]> {
  const { data } = await api.get('/cau/operations/queues');
  return unwrapList<CauOperationalQueue>(data);
}

export async function listQueueMembers(queueId: string): Promise<CauQueueMember[]> {
  const { data } = await api.get(`/cau/operations/queues/${queueId}/members`);
  return unwrapList<CauQueueMember>(data);
}

export async function listQueueCandidates(queueId: string): Promise<CauQueueCandidate[]> {
  const { data } = await api.get(`/cau/operations/queues/${queueId}/candidates`);
  return unwrapList<CauQueueCandidate>(data);
}

export async function addQueueMember(queueId: string, userId: string, role: CauQueueMember['role']) {
  const { data } = await api.post(`/cau/operations/queues/${queueId}/members`, { userId, role });
  return entity<CauQueueMember>(data);
}

export async function removeQueueMember(queueId: string, userId: string) {
  await api.delete(`/cau/operations/queues/${queueId}/members/${userId}`);
}

export async function listQueueTickets(queueId: string, params: Record<string, unknown> = {}): Promise<{ data: CauOperationalTicket[]; meta: CauTicketList['meta'] }> {
  const { data } = await api.get(`/cau/operations/queues/${queueId}/tickets`, { params });
  return { data: unwrapList<CauOperationalTicket>(data), meta: data.meta ?? { page: 1, pageSize: 20, total: 0, totalPages: 0 } };
}

export async function getOperationalTicket(id: string): Promise<CauOperationalTicket> {
  const { data } = await api.get(`/cau/operations/tickets/${id}`);
  return entity<CauOperationalTicket>(data);
}

async function operationalCommand<T = CauOperationalTicket>(path: string, body: Record<string, unknown> = {}) {
  const { data } = await api.post(path, body);
  return entity<T>(data);
}

export const claimTicket = (id: string) => operationalCommand(`/cau/operations/tickets/${id}/claim`);
export const startTicket = (id: string) => operationalCommand(`/cau/operations/tickets/${id}/start`);
export const resumeTicket = (id: string, reason: string) => operationalCommand(`/cau/operations/tickets/${id}/resume`, { reason });
export const waitTicket = (id: string, type: 'requester' | 'collaborator' | 'vendor', reason: string) => operationalCommand(`/cau/operations/tickets/${id}/wait`, { type, reason });
export const routeTicket = (id: string, input: { queueId: string; justification: string; impact?: CauTicketLevel; urgency?: CauTicketLevel; potentialIncident?: boolean; duplicateOfTicketId?: string }) => operationalCommand(`/cau/operations/tickets/${id}/route`, input);
export const assignTicket = (id: string, assigneeId: string, reason: string) => operationalCommand(`/cau/operations/tickets/${id}/assign`, { assigneeId, reason });
export const transferTicket = (id: string, destinationQueueId: string, justification: string) => operationalCommand<{ ticket: CauOperationalTicket }>(`/cau/operations/tickets/${id}/transfers`, { destinationQueueId, justification });
export const decideTicketTransfer = (transferId: string, decision: 'accept' | 'reject', reason: string, assigneeId?: string) => operationalCommand<{ ticket: CauOperationalTicket }>(`/cau/operations/transfers/${transferId}/${decision}`, { reason, assigneeId });
export const addTicketCollaboration = (id: string, queueId: string, reason: string, dueAt?: string) => operationalCommand(`/cau/operations/tickets/${id}/collaborations`, { queueId, reason, dueAt });
export const resolveTicket = (id: string, solution: string, evidence: string, attachmentIds: string[] = []) => operationalCommand(`/cau/operations/tickets/${id}/resolve`, { solution, evidence, attachmentIds });
export const cancelTicket = (id: string, reason: string) => operationalCommand(`/cau/operations/tickets/${id}/cancel`, { reason });

export async function listTicketInteractions(id: string): Promise<CauInteraction[]> {
  const { data } = await api.get(`/cau/tickets/${id}/interactions`); return unwrapList<CauInteraction>(data);
}
export async function addTicketMessage(id: string, body: string, correctsInteractionId?: string) {
  const { data } = await api.post(`/cau/tickets/${id}/interactions`, { body, correctsInteractionId }); return entity<CauInteraction>(data);
}
export async function provideTicketInformation(id: string, body: string) { const { data } = await api.post(`/cau/tickets/${id}/information`, { body }); return entity(data); }
export async function confirmTicketResolution(id: string) { const { data } = await api.post(`/cau/tickets/${id}/resolution/confirm`); return entity(data); }
export async function contestTicketResolution(id: string, reason: string) { const { data } = await api.post(`/cau/tickets/${id}/resolution/contest`, { reason }); return entity(data); }
export async function clarifyTicketResolution(id: string, reason: string) { const { data } = await api.post(`/cau/tickets/${id}/resolution/clarification`, { reason }); return entity(data); }
export async function reopenTicket(id: string, reason: string) { const { data } = await api.post(`/cau/tickets/${id}/reopen`, { reason }); return entity<{ mode: 'reopened'|'follow_up'; ticketId: string; protocol: string }>(data); }
export async function getSatisfactionSurvey(id: string): Promise<CauSatisfactionSurvey> { const { data } = await api.get(`/cau/tickets/${id}/satisfaction-survey`); return entity(data); }
export async function answerSatisfactionSurvey(id: string, answers: Record<string, unknown>) { const { data } = await api.post(`/cau/tickets/${id}/satisfaction`, { answers }); return entity(data); }
export async function listOperationalInteractions(id: string): Promise<CauInteraction[]> { const { data } = await api.get(`/cau/operations/tickets/${id}/interactions`); return unwrapList<CauInteraction>(data); }
export async function addOperationalMessage(id: string, input: { body: string; visibility: CauInteraction['visibility']; audienceUserIds?: string[]; classification?: string }) { const { data } = await api.post(`/cau/operations/tickets/${id}/interactions`, input); return entity<CauInteraction>(data); }
export async function requestTicketInformation(id: string, body: string) { const { data } = await api.post(`/cau/operations/tickets/${id}/request-information`, { body }); return entity(data); }

export async function listCauApprovals(): Promise<CauApprovalStep[]> { const { data } = await api.get('/cau/approvals'); return unwrapList<CauApprovalStep>(data); }
export async function decideCauApproval(id: string, decision: 'approve'|'reject'|'return'|'impediment'|'opinion', reason?: string) {
  const { data } = await api.post(`/cau/approval-steps/${id}/decisions`, { decision, reason }, { headers: { 'Idempotency-Key': crypto.randomUUID() } }); return entity(data);
}
export async function getCauTicketApproval(id: string) { const { data } = await api.get(`/cau/tickets/${id}/approval`); return entity(data); }
export async function upsertCauAccess(ticketId: string, input: CauAccessInput) { const { data } = await api.put(`/cau/tickets/${ticketId}/access-operation`, input); return entity<CauAccessRequest>(data); }
export async function getCauTicketAccess(ticketId: string) { const { data } = await api.get(`/cau/tickets/${ticketId}/access-operation`); return entity<CauAccessRequest & { grant?: Record<string, unknown>; executions?: Record<string, unknown>[] }>(data); }
export async function listMyCauAccesses(): Promise<{ requests: CauAccessRequest[]; grants: Array<Record<string, unknown>> }> { const { data } = await api.get('/cau/accesses/mine'); return entity(data); }
export async function listOperationalCauAccesses(): Promise<CauAccessRequest[]> { const { data } = await api.get('/cau/accesses'); return unwrapList<CauAccessRequest>(data); }
export async function provisionCauAccess(id: string, input: { evidence: Record<string, unknown>; executedProfile?: Record<string, unknown>; mfaEnabled?: boolean; monitoringReference?: string }) { const { data } = await api.post(`/cau/access-requests/${id}/provision`, input); return entity(data); }
export async function revokeCauAccess(id: string, evidence: Record<string, unknown>) { const { data } = await api.post(`/cau/access-requests/${id}/revoke`, { evidence }); return entity(data); }
export async function extendCauAccess(id: string, validUntil: string, justification: string) { const { data } = await api.post(`/cau/access-requests/${id}/extend`, { validUntil, justification }); return entity(data); }
export async function listMyCauAssets(): Promise<{current:CauAssetSummary[];history:Array<{id:string;asset:CauAssetSummary;custodian:{id:string;name:string};deliveredAt:string;returnedAt:string|null;expectedReturnAt:string|null;signatureStatus:string;term:{id:string;fileName:string}|null}>}> { const {data}=await api.get('/cau/assets/mine'); return entity(data); }
export async function searchCauAssets(q:string):Promise<CauAssetSummary[]>{const{data}=await api.get('/cau/assets/search',{params:{q}});return unwrapList<CauAssetSummary>(data);}
export async function searchCauAssetCustodians(q:string):Promise<CauAssetCustodian[]>{const{data}=await api.get('/cau/asset-custodians/search',{params:{q}});return unwrapList<CauAssetCustodian>(data);}
export async function listCauResources(ticketId:string):Promise<CauResourceLink[]>{const{data}=await api.get(`/cau/tickets/${ticketId}/resources`);return unwrapList<CauResourceLink>(data);}
export async function linkCauAsset(ticketId:string,assetId:string,isPrimary=true,justification?:string){const{data}=await api.post(`/cau/tickets/${ticketId}/resources`,{resourceType:'asset',externalId:assetId,isPrimary,justification});return entity<CauResourceLink>(data);}
export async function unlinkCauResource(ticketId:string,linkId:string,justification:string){const{data}=await api.delete(`/cau/tickets/${ticketId}/resources/${linkId}`,{data:{justification}});return entity(data);}
export async function listCauAssetOperations(ticketId:string):Promise<CauAssetOperation[]>{const{data}=await api.get(`/cau/tickets/${ticketId}/asset-operations`);return unwrapList<CauAssetOperation>(data);}
export async function createCauAssetOperation(ticketId:string,input:{resourceLinkId:string;type:string;targetCustodianId?:string;replacementAssetId?:string;relatedPurchaseRequestId?:string;temporaryUntil?:string;details?:Record<string,unknown>;evidenceAttachmentIds?:string[]}){const{data}=await api.post(`/cau/tickets/${ticketId}/asset-operations`,input);return entity<CauAssetOperation>(data);}
export async function acceptCauAssetTransfer(id:string,action:'accept_current'|'accept_target'|'accept_terms'|'confirm_inspection'|'reject',reason?:string){const{data}=await api.post(`/cau/asset-operations/${id}/acceptance`,{action,reason});return entity<CauAssetOperation>(data);}
export async function executeCauAssetOperation(id:string,evidence:Record<string,unknown>){const{data}=await api.post(`/cau/asset-operations/${id}/execute`,{evidence});return entity<CauAssetOperation>(data);}
export async function addCauAssetContainment(id:string,input:{action:string;description:string;dueAt?:string}){const{data}=await api.post(`/cau/asset-operations/${id}/containments`,input);return entity<CauAssetContainment>(data);}
export async function completeCauAssetContainment(id:string,evidence:Record<string,unknown>){const{data}=await api.post(`/cau/asset-containments/${id}/complete`,{evidence});return entity<CauAssetContainment>(data);}
export async function getCauNotificationPreferences():Promise<CauNotificationPreference>{const{data}=await api.get('/cau/notifications/preferences');return entity(data);}
export async function updateCauNotificationPreferences(input:Partial<Pick<CauNotificationPreference,'emailEnabled'|'teamsEnabled'|'digestEnabled'|'mutedEventTypes'>>):Promise<CauNotificationPreference>{const{data}=await api.patch('/cau/notifications/preferences',input);return entity(data);}
export async function listMyCauNotificationDeliveries():Promise<CauNotificationDelivery[]>{const{data}=await api.get('/cau/notifications/deliveries');return unwrapList(data);}
export async function listCauAuditEvents(params:Record<string,unknown>={}):Promise<{data:CauAuditEvent[];meta:{page:number;pageSize:number;total:number;totalPages:number}}>{const{data}=await api.get('/cau/audit/events',{params});return{data:unwrapList<CauAuditEvent>(data),meta:data.meta??{page:1,pageSize:25,total:0,totalPages:0}};}
export async function verifyCauAuditIntegrity():Promise<{valid:boolean;checked:number;legacy:number;failures:string[]}>{const{data}=await api.get('/cau/audit/integrity');return entity(data);}
export async function getCauDashboard(params:CauAnalyticsFilters={}):Promise<CauDashboard>{const{data}=await api.get('/cau/dashboard',{params});return entity(data);}
export async function getCauDashboardDrilldown(params:CauAnalyticsFilters&{dimension?:string;value?:string;page?:number;pageSize?:number}):Promise<{data:CauTicket[];meta:CauTicketList['meta'];methodologyVersion:string}>{const{data}=await api.get('/cau/dashboard/drilldown',{params});return{data:unwrapList<CauTicket>(data),meta:data.meta,methodologyVersion:data.methodologyVersion};}
export async function createCauReport(input:CauAnalyticsFilters&{reportType:'tickets'|'sla'|'satisfaction';format:'csv'|'xlsx'|'pdf';columns?:string[];purpose:string}):Promise<CauReportJob>{const{data}=await api.post('/cau/reports',input);return entity(data);}
export async function listCauReports():Promise<CauReportJob[]>{const{data}=await api.get('/cau/reports');return unwrapList(data);}
export async function downloadCauReport(job:CauReportJob){const response=await api.get(`/cau/reports/${job.id}/download`,{responseType:'blob'});const url=URL.createObjectURL(response.data);const anchor=document.createElement('a');anchor.href=url;anchor.download=job.fileName??`relatorio-cau.${job.format}`;anchor.click();URL.revokeObjectURL(url);}
export async function createCauReportSchedule(input:CauAnalyticsFilters&{name:string;reportType:'tickets'|'sla'|'satisfaction';format:'csv'|'xlsx'|'pdf';purpose:string;frequency:'daily'|'weekly'|'monthly';nextRunAt:string;recipientUserIds:string[]}):Promise<CauReportSchedule>{const{data}=await api.post('/cau/report-schedules',input);return entity(data);}
export async function listCauReportSchedules():Promise<CauReportSchedule[]>{const{data}=await api.get('/cau/report-schedules');return unwrapList(data);}

